import { memo, useMemo } from "react";
import { useTermsDraft } from "../../store/useTermsDraftStore";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { TermsMarkdown } from "../../components/request/TermsMarkdown";
import { PAYOUT_CLAIM_ROUTES, TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";

/**
 * Fund-request welcome / consent screen. Renders the partner-configured Terms &
 * Conditions entries (seeded in the mock session) as one checkbox each, with a
 * markdown intro and a navigable detail. Falls back to the legacy E-Sign +
 * Terms checkboxes when no dynamic terms are present.
 *
 * Gallery: there is no `acceptTerms` API — accepting simply navigates to the
 * payout-claim screen.
 */
export const WelcomePage = memo(() => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, dropzoneColors, backgroundIcon } = colorConfig;

  const entries = useMemo(
    () => session.data?.terms?.entries ?? [],
    [session.data?.terms]
  );
  const hasDynamicTerms = entries.length > 0;

  const {
    checkedById,
    isESignChecked,
    isTermsChecked,
    setEntryChecked,
    setESignChecked: setIsESignChecked,
    setTermsChecked: setIsTermsChecked,
    reset: resetTermsDraft,
  } = useTermsDraft("payout-claim");

  const allRequiredAccepted = useMemo(() => {
    if (hasDynamicTerms) {
      return entries
        .filter((entry) => entry.required !== false)
        .every((entry) => checkedById[entry.id]);
    }
    return isESignChecked && isTermsChecked;
  }, [hasDynamicTerms, entries, checkedById, isESignChecked, isTermsChecked]);

  const handleContinue = () => {
    if (!allRequiredAccepted) return;
    resetTermsDraft();
    navigate(PAYOUT_CLAIM_ROUTES.CLAIM_ENTRY);
  };

  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Content className="landing-section welcome-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">

          <FlowHeroMark backgroundIcon={backgroundIcon}>

            <CashSpecialIcon size={60} colors={dropzoneColors} />

          </FlowHeroMark>
        <section className="landing-container">
          <PageTitle
            highlight={t("welcome.highlight")}
            highlightColor={TITLE_COLOR}
            secudnary={` ${t("welcome.secondary")}`}
            secudnaryColor={TITLE_COLOR}
          />
        </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          {hasDynamicTerms ? (
            entries.map((entry) => (
              <section className="landing-policies" key={entry.id}>
                <span
                  className={`policy-checkbox${
                    entry.required !== false && !checkedById[entry.id]
                      ? " policy-checkbox--required"
                      : ""
                  }`}
                >
                  <input
                    id={`term-${entry.id}`}
                    type="checkbox"
                    checked={!!checkedById[entry.id]}
                    onChange={(e) =>
                      setEntryChecked(entry.id, e.target.checked)
                    }
                  />
                </span>
                <div className="footer-text terms-text footer-link muted-foreground">
                  <label htmlFor={`term-${entry.id}`}>
                    <TermsMarkdown content={entry.introMarkdown} inline />
                  </label>{" "}
                  {entry.detailMarkdown && (
                    <GradientText
                      as="label"
                      className="link"
                      textColor={title}
                      onClick={() =>
                        navigate(
                          `${PAYOUT_CLAIM_ROUTES.TERM_DETAIL}?entryId=${entry.id}`
                        )
                      }
                    >
                      {t("terms.viewDetails")}
                    </GradientText>
                  )}
                </div>
              </section>
            ))
          ) : (
            <>
              <section className="landing-policies">
                <span
                  className={`policy-checkbox${
                    isESignChecked ? "" : " policy-checkbox--required"
                  }`}
                >
                  <input
                    id="welcome-e-sign"
                    type="checkbox"
                    checked={isESignChecked}
                    onChange={(e) => setIsESignChecked(e.target.checked)}
                  />
                </span>
                <BodyCopy className="footer-text terms-text footer-link muted-foreground">
                  <label htmlFor="welcome-e-sign">
                    {t("welcome.esignContent")}
                  </label>
                  <GradientText
                    as="label"
                    className="link"
                    textColor={title}
                    onClick={() => navigate(`${PAYOUT_CLAIM_ROUTES.LEGAL}?doc=esign`)}
                  >
                    {t("welcome.esignLink")}
                  </GradientText>
                </BodyCopy>
              </section>

              <section className="landing-policies">
                <span
                  className={`policy-checkbox${
                    isTermsChecked ? "" : " policy-checkbox--required"
                  }`}
                >
                  <input
                    id="welcome-terms"
                    type="checkbox"
                    checked={isTermsChecked}
                    onChange={(e) => setIsTermsChecked(e.target.checked)}
                  />
                </span>
                <BodyCopy className="footer-text terms-text footer-link muted-foreground">
                  <label htmlFor="welcome-terms">
                    {t("welcome.termsContent")}
                  </label>
                  <GradientText
                    as="label"
                    className="link"
                    textColor={title}
                    onClick={() =>
                      navigate(`${PAYOUT_CLAIM_ROUTES.LEGAL}?doc=userAgreement`)
                    }
                  >
                    {t("welcome.termsLink")}
                  </GradientText>
                  <label>{t("welcome.termsContent2")}</label>
                </BodyCopy>
              </section>
            </>
          )}

          <Button
            size="large"
            disabled={!allRequiredAccepted}
            background={backgroundBtn}
            color={textColorBtn}
            onClick={handleContinue}
          >
            {t("welcome.accept")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
